/**
 * RulesetEditor — displays the active PM notification ruleset and lets
 * the admin edit + save a new version.
 *
 * Permission model:
 *   - Any authenticated user may READ the ruleset body.
 *   - Only the admin may WRITE (backend enforces 403; FE additionally
 *     renders the textarea read-only and hides the Save button).
 *
 * Receives `canEdit` from the parent so the admin gate is evaluated once
 * in the page and threaded down — mirroring the Memory dashboard pattern.
 */

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useEffect, useState, type ReactElement } from 'react';

import { usePmRuleset } from '@/hooks/usePmRuleset';

interface Props {
  canEdit: boolean;
}

export function RulesetEditor({ canEdit }: Props): ReactElement {
  const { ruleset, isLoading, error, save, isSaving, saveError, clearSaveError } =
    usePmRuleset();

  // Local draft of the textarea — initialised from the loaded ruleset.
  const [draft, setDraft] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [savedVersion, setSavedVersion] = useState<number | null>(null);

  // Sync draft when the ruleset first loads (or is refreshed after save).
  useEffect(() => {
    if (ruleset != null) {
      setDraft(ruleset.body);
    }
  }, [ruleset]);

  const handleSaveClick = () => {
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    try {
      const updated = await save(draft);
      setSavedVersion(updated.version);
    } catch {
      // saveError is already surfaced via the hook; nothing else to do here.
    }
  };

  const handleCancel = () => {
    setConfirmOpen(false);
  };

  // --- loading state ---
  if (isLoading) {
    return (
      <Box role="status" aria-label="Loading ruleset">
        <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={320} />
      </Box>
    );
  }

  // --- error state ---
  if (error) {
    return (
      <Alert severity="error" role="alert" sx={{ mb: 2 }}>
        Failed to load ruleset: {error}
      </Alert>
    );
  }

  const isDirty = ruleset != null && draft !== ruleset.body;

  return (
    <Box>
      {/* Metadata row */}
      {ruleset && (
        <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            version{' '}
            <Typography
              component="span"
              variant="body2"
              sx={{ fontFamily: 'monospace', fontWeight: 700 }}
            >
              {ruleset.version}
            </Typography>
          </Typography>
          {ruleset.updated_by && (
            <Typography variant="body2" color="text.secondary">
              by {ruleset.updated_by}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {new Date(ruleset.updated_at).toLocaleString()}
          </Typography>
        </Stack>
      )}

      {/* Success notice */}
      {savedVersion != null && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSavedVersion(null)}>
          Saved as version {savedVersion}.
        </Alert>
      )}

      {/* Save error */}
      {saveError && (
        <Alert
          severity="error"
          role="alert"
          sx={{ mb: 2 }}
          onClose={clearSaveError}
        >
          {saveError}
        </Alert>
      )}

      {/* Confirm prompt replaces the toolbar when active */}
      {confirmOpen ? (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                color="warning"
                onClick={() => void handleConfirm()}
                disabled={isSaving}
                startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : undefined}
                aria-label="Confirm save ruleset"
              >
                {isSaving ? 'Saving…' : 'Confirm'}
              </Button>
              <Button size="small" onClick={handleCancel} aria-label="Cancel save ruleset">
                Cancel
              </Button>
            </Stack>
          }
        >
          This will create a new ruleset version. The old version is retained in the
          audit log but will no longer be active. Continue?
        </Alert>
      ) : (
        canEdit && (
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<SaveIcon />}
              disabled={!isDirty || isSaving}
              onClick={handleSaveClick}
              aria-label="Save ruleset"
            >
              Save new version
            </Button>
          </Stack>
        )
      )}

      {/* Ruleset body — editable for admins, read-only for others */}
      <TextField
        id="pm-ruleset-body"
        label="Ruleset body"
        multiline
        minRows={18}
        fullWidth
        value={draft}
        onChange={(e) => {
          clearSaveError();
          setDraft(e.target.value);
        }}
        inputProps={{
          readOnly: !canEdit,
          'aria-label': canEdit ? 'Edit ruleset body' : 'Ruleset body (read-only)',
          style: { fontFamily: 'monospace', fontSize: '0.8125rem' },
        }}
        helperText={
          !canEdit
            ? 'Read-only — admin only'
            : isDirty
              ? 'Unsaved changes'
              : 'No unsaved changes'
        }
        size="small"
      />
    </Box>
  );
}
